import { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	Gender,
	VALID_ABHYASI_ID_REGEX,
	VALID_EMAIL_REGEX,
	VALID_PHONE_REGEX,
	VALID_STRING_INPUT_REGEX,
} from '../../../shared/Constants';
import { useAuth } from '../../../hooks/useAuth';
import { useSelfRegistration } from './useSelfRegistration';
import { UpdateHFNProfileStatement } from '../../../components/UpdateHFNProfileStatement';

export const SelfRegistration = () => {
	const [form] = Form.useForm();
	const { t } = useTranslation();
	const { mySrcmProfile } = useAuth();
	const {
		onCancelClick,
		submitButtonDisabled,
		onSubmitClick,
		handleFormValuesChange,
		zoneAuthorizedFor,
		getHomeZoneandRegion,
	} = useSelfRegistration({ form });

	const [registerRole, setRegisterRole] = useState('');
	const [zoneOptions, setZoneOptions] = useState<string[]>([]);

	const fetchProfileDetails = useCallback(async () => {
		const storedRole = localStorage.getItem('registerRole') ?? '';
		setRegisterRole(storedRole);

		if (!mySrcmProfile?.srcm_group?.id) {
			throw new Error('srcm_group.id is undefined!');
		}

		const getZoneDetails = await getHomeZoneandRegion(
			mySrcmProfile.srcm_group.id
		);

		form.setFieldsValue({
			registerAs: t(storedRole),
			firstName: mySrcmProfile?.first_name ?? '',
			lastName: mySrcmProfile?.last_name ?? '',
			email: mySrcmProfile?.email ?? '',
			mobile: mySrcmProfile?.mobile ?? '',
			srcmId: mySrcmProfile?.ref ?? '',
			gender:
				mySrcmProfile?.gender === 'M'
					? Gender.MALE
					: mySrcmProfile?.gender === 'F'
					? Gender.FEMALE
					: '',
			zoneHome: getZoneDetails.homeZone ?? '',
			homeZone: {
				regionId: Number(getZoneDetails.homeRegionId),
				regionName: getZoneDetails.homeRegion ?? '',
			},
		});

		if (getZoneDetails.homeRegionId) {
			const parsedRegionId = Number(getZoneDetails.homeRegionId);

			const zoneDetails = await zoneAuthorizedFor(parsedRegionId);

			if (zoneDetails?.results?.length) {
				const allZoneNames = zoneDetails.results
					.map((zone) => zone.name)
					.filter((name): name is string => Boolean(name)); // Remove undefined

				const filteredZones = allZoneNames.filter(
					(zoneName) =>
						zoneName.trim().toUpperCase() !==
						(getZoneDetails?.homeZone || '').trim().toUpperCase()
				);

				form.setFieldsValue({ zone: filteredZones });
				setZoneOptions(filteredZones);
			}
		}

		await handleFormValuesChange();
	}, [
		form,
		handleFormValuesChange,
		mySrcmProfile,
		zoneAuthorizedFor,
		t,
		getHomeZoneandRegion,
	]);

	useEffect(() => {
		void fetchProfileDetails();
	}, [fetchProfileDetails]);

	return (
		<Form
			name="basic"
			layout="vertical"
			size="middle"
			labelCol={{ span: 16 }}
			initialValues={{ remember: true }}
			autoComplete="off"
			form={form}
		>
			<div className="relative flex items-center justify-center border-b-[2px] border-gray-700 pb-6 mt-4">
				{/* Title - centered */}
				<div className="text-center font-sans font-bold text-lg">
					{t('selfRegistrationForm')}
				</div>
			</div>
			<div className="flex sm:justify-left md:justify-center md:items-left">
				<div className="bg-transparent md:bg-white sm:p-0 md:p-4 sm:rounded-none md:rounded-lg sm:border-none md:border md:border-solid md:border-gray-200 w-full md:max-w-2xl mx-auto">
					<div className="flex flex-wrap overflow-y-auto pb-[80px] md:pb-[10px]">
						<div className="w-full px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('registerAs')}
										<span className="text-red"> *</span>
									</span>
								}
								name="registerAs"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									maxLength={100}
									className="flex min-w-[200px]"
									size="large"
									disabled
									value={registerRole}
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('firstName')}
										<span className="text-red"> *</span>
									</span>
								}
								name="firstName"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_STRING_INPUT_REGEX,
										message: <span>{t('onlyAllowedSpecialCharacters')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									maxLength={100}
									className="flex min-w-[200px]"
									size="large"
									disabled
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('lastName')}
										<span className="text-red"> *</span>
									</span>
								}
								labelCol={{ flex: '100%' }}
								name="lastName"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_STRING_INPUT_REGEX,
										message: <span>{t('onlyAllowedSpecialCharacters')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									maxLength={100}
									className="flex min-w-[200px]"
									size="large"
									disabled
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650  font-sans">
										{t('email')}
										<span className="text-red"> *</span>
									</span>
								}
								name="email"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_EMAIL_REGEX,
										message: <span>{t('invalidEmail')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input className="flex min-w-[200px]" size="large" disabled />
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650  font-sans">
										{t('phoneNo')}
										<span className="text-red"> *</span>
									</span>
								}
								name="mobile"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_PHONE_REGEX,
										message: <span>{t('invalidPhoneNo')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input className="flex min-w-[200px]" size="large" disabled />
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('SRCMID')}
										<span className="text-red"> *</span>
									</span>
								}
								name="srcmId"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
									{
										pattern: VALID_ABHYASI_ID_REGEX,
										message: <span>{t('invalidSRCMID')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input
									maxLength={20}
									disabled
									className="flex min-w-[200px]"
									size="large"
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('gender')}
										<span className="text-red"> *</span>
									</span>
								}
								name="gender"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
								]}
								required={false}
								className="mb-[16px]"
							>
								<Input className="flex min-w-[200px]" size="large" disabled />
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('homeRegion')}
										<span className="text-red"> *</span>
									</span>
								}
								name={['homeZone', 'regionName']}
								className="mb-[16px]"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
								]}
								required={false}
							>
								<Input
									maxLength={20}
									disabled
									className="flex min-w-[200px]"
									size="large"
								/>
							</Form.Item>
						</div>
						<div className="w-full md:w-1/2 px-2 mb-4">
							<Form.Item
								label={
									<span className="text-gray-650 font-sans">
										{t('homeZone')}
										<span className="text-red"> *</span>
									</span>
								}
								name="zoneHome"
								required={false}
								className="mb-[16px]"
								rules={[
									{
										required: true,
										message: <span>{t('required')}</span>,
									},
								]}
							>
								<Input
									maxLength={20}
									disabled
									className="flex min-w-[200px]"
									size="large"
								/>
							</Form.Item>
						</div>
						{registerRole == 'proposer' && (
							<>
								<div className="w-full px-2 mb-4">
									<Form.Item
										label={
											<span className="text-gray-650 font-sans">
												{t('regionAuthorizedFor')}
												<span className="text-red"> *</span>
											</span>
										}
										name={['homeZone', 'regionName']}
										required={false}
										className="mb-[16px]"
										rules={[
											{
												required: true,
												message: <span>{t('required')}</span>,
											},
										]}
									>
										<Input
											maxLength={20}
											disabled
											className="flex min-w-[200px]"
											size="large"
										/>
									</Form.Item>
								</div>
								<div className="w-full  px-2 mb-4">
									<Form.Item
										label={
											<span className="text-gray-650 font-sans">
												{t('zoneAuthorizedFor')}
												<span className="text-red"> *</span>
											</span>
										}
										name="zone"
										className="mb-[16px]"
										required={false}
										rules={[
											{
												required: true,
												message: <span>{t('required')}</span>,
											},
										]}
									>
										<Select
											mode="multiple"
											disabled
											size="large"
											className="flex min-w-[200px]"
											options={zoneOptions.map((z) => ({
												label: z,
												value: z,
											}))}
										/>
									</Form.Item>
								</div>
							</>
						)}
						<div className="w-full px-2 mb-4">
							<UpdateHFNProfileStatement />
						</div>
					</div>
					<div className="flex flex-row py-[24px] px-[16px] fixed md:relative bottom-0 left-0 right-0 md:p-[8px] justify-center  bg-white md:bg-transparent mt-[16px] md:mt-[12px] mb-[8px] md:mb-[0px] mobile-top-shadow">
						<Button
							onClick={onCancelClick}
							className="w-[180px] h-[36px] md:w-[140px] border-solid border-primary-blue text-primary-blue"
							variant="outlined"
						>
							{t('cancel')}
						</Button>
						<Button
							type="primary"
							disabled={submitButtonDisabled}
							className="w-[180px] h-[36px] md:w-[140px] ml-[16px]"
							onClick={onSubmitClick}
						>
							{t('submit')}
						</Button>
					</div>
				</div>
			</div>
		</Form>
	);
};
